import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { TrendingUp, AlertTriangle, DollarSign, GitBranch, Table, Network } from 'lucide-react';
import KPICard from '../components/KPICard';
import Timeline from '../components/Timeline';
import DependenciesGraph from '../components/DependenciesGraph';
import { api } from '../api/client';
import { useFilters } from '../context/FilterContext';
import { useAuth } from '../context/AuthContext';
import { ProgramResourceSection } from '../components/ResourceDashboardWidgets';

const ProgramDashboard = () => {
  const { programId } = useParams();
  const navigate = useNavigate();
  const { filters, getFilterParams } = useFilters();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [dependencyView, setDependencyView] = useState('table'); // 'table' or 'graph'
  
  const tenantId = user?.tenant?.id || user?.tenant_id;

  useEffect(() => {
    loadDashboard();
  }, [programId, filters]);

  const loadDashboard = async () => {
    try {
      setLoading(true);
      const filterParams = getFilterParams();
      const response = await api.getProgramDashboard(programId, filterParams);
      setData(response.data);
    } catch (error) {
      console.error('Failed to load program dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleProjectClick = (project) => {
    navigate(`/project/${project.id}`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[var(--clr-brand-primary)]"></div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex items-center justify-center h-screen">
        <p className="text-[var(--clr-text-muted)]">Failed to load dashboard data</p>
      </div>
    );
  }

  const { program, kpis, project_roadmap, dependencies, team_wip } = data;

  return (
    <div className="min-h-screen bg-[var(--clr-surface-subtle)]">
      <div className="max-w-[1380px] mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate(-1)}
            className="text-[var(--clr-brand-primary)] hover:text-blue-700 text-sm font-medium mb-2"
            data-testid="back-button"
          >
            ← Back
          </button>
          <h1 className="text-3xl font-bold text-[var(--clr-text-primary)] mb-2">{program.name}</h1>
          <p className="text-[var(--clr-text-secondary)]">Program Dashboard</p>
        </div>

        {/* Filters - Commented out due to unresolved setFilters reference error */}
        {/* <FilterBar onFilterChange={setFilters} initialFilters={filters} /> */}

        {/* KPIs */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <KPICard
            data-testid="projects-kpi"
            title="Projects In-Flight"
            value={kpis.project_count}
            icon={<TrendingUp className="w-8 h-8 text-[var(--clr-brand-primary)]" />}
          />
          <KPICard
            data-testid="completion-kpi"
            title="% Complete"
            value={`${kpis.percent_complete}%`}
            icon={<TrendingUp className="w-8 h-8 text-[var(--clr-sev-healthy)]" />}
          />
          <KPICard
            data-testid="blocked-kpi"
            title="Blocked WIP"
            value={kpis.blocked_count}
            subtitle="Items blocked"
            icon={<AlertTriangle className="w-8 h-8 text-[var(--clr-sev-blocked)]" />}
          />
          <KPICard
            data-testid="cost-variance-kpi"
            title="Cost Variance"
            value={`$${(kpis.cost_metrics.variance / 1000).toFixed(1)}K`}
            subtitle={`${kpis.cost_metrics.variance_pct > 0 ? '+' : ''}${kpis.cost_metrics.variance_pct}%`}
            icon={<DollarSign className="w-8 h-8 text-purple-600" />}
          />
        </div>

        {/* Project Roadmap */}
        <div className="bg-[var(--clr-surface-card)] rounded-xl border border-gray-200 p-6 shadow-[var(--shadow-card)] mb-8">
          <h2 className="text-xl font-semibold text-[var(--clr-text-primary)] mb-4" data-testid="project-roadmap-title">Project Roadmap</h2>
          {project_roadmap && project_roadmap.length > 0 ? (
            <Timeline 
              data={project_roadmap} 
              onItemClick={handleProjectClick}
            />
          ) : (
            <p className="text-[var(--clr-text-muted)] text-center py-8">No project data available</p>
          )}
        </div>

        {/* Dependencies & Team WIP */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Dependencies */}
          <div className="bg-[var(--clr-surface-card)] rounded-xl border border-gray-200 p-6 shadow-[var(--shadow-card)]">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-[var(--clr-text-primary)]" data-testid="dependencies-title">
                <GitBranch className="inline w-5 h-5 mr-2" />
                Dependencies
              </h2>
              {/* View Toggle */}
              <div className="flex items-center bg-[var(--clr-surface-subtle)] rounded-lg p-1">
                <button
                  onClick={() => setDependencyView('table')}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    dependencyView === 'table'
                      ? 'bg-white text-[var(--clr-text-primary)] shadow-[var(--shadow-card)]'
                      : 'text-[var(--clr-text-secondary)] hover:text-[var(--clr-text-primary)]'
                  }`}
                  data-testid="dependency-table-view-btn"
                >
                  <Table className="w-4 h-4" />
                  Table
                </button>
                <button
                  onClick={() => setDependencyView('graph')}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    dependencyView === 'graph'
                      ? 'bg-white text-[var(--clr-text-primary)] shadow-[var(--shadow-card)]'
                      : 'text-[var(--clr-text-secondary)] hover:text-[var(--clr-text-primary)]'
                  }`}
                  data-testid="dependency-graph-view-btn"
                >
                  <Network className="w-4 h-4" />
                  Graph
                </button>
              </div>
            </div>
            
            {dependencyView === 'table' ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[var(--clr-surface-subtle)]">
                    <tr>
                      <th className="px-4 py-2 text-left font-medium text-gray-700">From</th>
                      <th className="px-4 py-2 text-left font-medium text-gray-700">To</th>
                      <th className="px-4 py-2 text-left font-medium text-gray-700">Type</th>
                      <th className="px-4 py-2 text-left font-medium text-gray-700">Aging</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {dependencies && dependencies.length > 0 ? (
                      dependencies.map((dep) => (
                        <tr key={dep.id} className="hover:bg-[var(--clr-surface-hover)]" data-testid={`dependency-${dep.id}`}>
                          <td className="px-4 py-3 text-[var(--clr-text-primary)]">{dep.from_project_name}</td>
                          <td className="px-4 py-3 text-[var(--clr-text-primary)]">{dep.to_project_name}</td>
                          <td className="px-4 py-3">
                            <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                              dep.dep_type === 'blocks' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'
                            }`}>
                              {dep.dep_type}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-[var(--clr-text-secondary)]">{dep.aging} days</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="4" className="px-4 py-8 text-center text-[var(--clr-text-muted)]">
                          No dependencies
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            ) : (
              <DependenciesGraph 
                dependencies={dependencies} 
                projects={project_roadmap}
              />
            )}
          </div>

          {/* Team WIP */}
          <div className="bg-[var(--clr-surface-card)] rounded-xl border border-gray-200 p-6 shadow-[var(--shadow-card)]">
            <h2 className="text-xl font-semibold text-[var(--clr-text-primary)] mb-4" data-testid="team-wip-title">Team WIP Snapshot</h2>
            <div className="space-y-3">
              {team_wip && team_wip.length > 0 ? (
                team_wip.map((team, idx) => (
                  <div 
                    key={idx}
                    data-testid={`team-wip-${team.team}`}
                    className="flex items-center justify-between p-4 bg-[var(--clr-surface-subtle)] rounded-lg"
                  >
                    <span className="text-sm font-medium text-[var(--clr-text-primary)]">{team.team}</span>
                    <span className="text-lg font-semibold text-[var(--clr-brand-primary)]">{team.count}</span>
                  </div>
                ))
              ) : (
                <p className="text-[var(--clr-text-muted)] text-center py-4">No WIP data</p>
              )}
            </div>
          </div>
        </div>

        {/* P6: Resource Management Section */}
        {tenantId && (
          <div className="mt-8">
            <ProgramResourceSection 
              tenantId={tenantId}
              programId={programId}
              onNavigate={navigate}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ProgramDashboard;
